import React, { useContext, useState, useRef } from 'react';
import { AppContext } from '../App';
import { getUploadUrl, uploadReceiptToS3, scanReceipt } from '../utils/api';

const DEMO_SCAN_RESULT = {
  vendor: "Home Depot #4231",
  date: new Date().toISOString().slice(0, 10),
  total: 347.82,
  items: ["2x4 Lumber x12 — $48.00","3/4 Plywood sheets x4 — $156.00","Box screws 3in — $24.99","Drywall compound — $31.47","Paint rollers x6 — $18.99","Subtotal: $279.45","Tax: $22.37","Total: $347.82"]
};

export default function ScanReceipt() {
  const { demoMode, showToast, navigate } = useContext(AppContext);
  const fileRef = useRef();
  const cameraRef = useRef();
  const [stage, setStage] = useState('idle');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) { showToast('Please select an image file', 'error'); return; }
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);

    if (demoMode) {
      setStage('uploading'); setProgress(30); await delay(600);
      setProgress(60); setStage('scanning'); await delay(800);
      setProgress(100); setResult(DEMO_SCAN_RESULT); setStage('done');
      return;
    }
    try {
      setStage('uploading'); setProgress(20);
      const { url, key } = await getUploadUrl(file.name, file.type);
      setProgress(40);
      await uploadReceiptToS3(url, file);
      setProgress(65);
      setStage('scanning');
      const scanResult = await scanReceipt(key);
      setProgress(100);
      setResult(scanResult); setStage('done');
    } catch (err) { setStage('error'); showToast('Scan failed — check AWS configuration', 'error'); }
  };

  const handleUseResult = () => { navigate('add'); showToast('Amount pre-filled from receipt'); };
  const reset = () => { setStage('idle'); setPreview(null); setResult(null); setProgress(0); };

  return (
    <div className="fade-in">
      <div className="screen-header" style={{ paddingBottom: 8 }}>
        <div><div className="screen-title">SCAN RECEIPT</div><div className="screen-subtitle">Auto-read vendor, date & total</div></div>
      </div>

      {demoMode && <div className="demo-banner">⚡ Demo: tap to simulate an AWS Textract scan</div>}

      {stage === 'idle' && (
        <>
          <div className="scan-area" onClick={() => fileRef.current?.click()}>
            <div className="scan-area__icon">📷</div>
            <div className="scan-area__title">Photograph a receipt</div>
            <div className="scan-area__sub">Tap to choose a photo or take a new one</div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
          <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className="btn btn--primary" onClick={() => cameraRef.current?.click()}>📷 Take Photo Now</button>
            <button className="btn btn--secondary" onClick={() => fileRef.current?.click()}>🖼️ Choose from Library</button>
          </div>
          <div className="card" style={{ marginTop: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>How it works</div>
            {[['📤','Upload','Photo goes directly to your private S3 bucket'],['🤖','Scan','AWS Textract reads the receipt text'],['✅','Review','Check the extracted data and save as an expense']].map(([icon,title,desc]) => (
              <div key={title} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 20, flexShrink: 0, width: 32, textAlign: 'center' }}>{icon}</div>
                <div><div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{desc}</div></div>
              </div>
            ))}
          </div>
        </>
      )}

      {(stage === 'uploading' || stage === 'scanning') && (
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          {preview && (
            <div style={{ marginBottom: 24, position: 'relative', display: 'inline-block' }}>
              <img src={preview} alt="Receipt" style={{ width: 200, height: 260, objectFit: 'cover', borderRadius: 12, opacity: 0.6 }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 12, background: 'rgba(15,25,35,0.5)' }}>
                <div className="spinner" style={{ width: 40, height: 40 }} />
              </div>
            </div>
          )}
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{stage === 'uploading' ? 'Uploading to AWS...' : 'Reading receipt...'}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>{stage === 'uploading' ? 'Sending to S3' : 'AWS Textract is scanning your receipt'}</div>
          <div className="progress-bar" style={{ height: 8, maxWidth: 240, margin: '0 auto' }}>
            <div className="progress-fill" style={{ width: `${progress}%`, background: 'var(--accent-orange)', transition: 'width 0.4s ease' }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>{progress}%</div>
        </div>
      )}

      {stage === 'done' && result && (
        <div style={{ padding: '0 16px' }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {preview && <img src={preview} alt="Receipt" style={{ width: 80, height: 100, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--accent-green)', marginBottom: 6 }}>✓ Receipt Read</div>
              {result.vendor && (<div><div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Vendor</div><div style={{ fontSize: 15, fontWeight: 600 }}>{result.vendor}</div></div>)}
              {result.date && (<div style={{ marginTop: 6 }}><div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date</div><div style={{ fontSize: 14 }}>{result.date}</div></div>)}
            </div>
          </div>
          {result.total && (
            <div style={{ background: 'rgba(58,230,126,0.08)', border: '1px solid rgba(58,230,126,0.25)', borderRadius: 12, padding: '16px', textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: 'var(--accent-green)', letterSpacing: 1, marginBottom: 4 }}>TOTAL DETECTED</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 36, color: 'var(--text-primary)' }}>${Number(result.total).toFixed(2)}</div>
            </div>
          )}
          {result.items && result.items.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>Line Items</div>
              {result.items.map((item, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '5px 0', borderBottom: i < result.items.length - 1 ? '1px solid var(--border)' : 'none', fontFamily: item.includes('Total') ? 'var(--font-mono)' : 'var(--font-body)', fontWeight: item.includes('Total') ? 600 : 400, color: item.includes('Total') ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{item}</div>
              ))}
            </div>
          )}
          <button className="btn btn--primary" onClick={handleUseResult} style={{ marginBottom: 10 }}>Use This — Save as Expense</button>
          <button className="btn btn--secondary" onClick={reset}>Scan Another Receipt</button>
        </div>
      )}

      {stage === 'error' && (
        <div style={{ padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Scan Failed</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>Make sure AWS Textract is enabled in your account and your Lambda has the right permissions.</div>
          <button className="btn btn--primary" onClick={reset}>Try Again</button>
        </div>
      )}
      <div style={{ height: 20 }} />
    </div>
  );
}

function delay(ms) { return new Promise(resolve => setTimeout(resolve, ms)); }
